import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import Logs from '@/components/common/Logs';
import ViewDocumentModal from '@/components/common/ViewDocumentModal';
import DocumentIcon from '@/components/Icons/DocumentIcon';
import Layout from '@/components/Layout/Layout';
import FullPageLoader from '@/components/loader/FullPageLoader';
import ApproveModal from '@/components/Modal/ApproveModal';
import ConfirmModal from '@/components/Modal/ConfirmModal';
import BackButton from '@/components/ui/BackButton';
import MarkUnpaidLeaveModal from '@/components/Modal/MarkUnpaidLeaveModal';
import EditIcon from '@/components/Icons/EditIcon';
import DeleteIcon from '@/components/Icons/DeleteIcon';
import { IDeleteResponse } from '@/interfaces/common.model';
import { IUser } from '@/interfaces/user.model';
import {
  calculateDaysDifference,
  handleStatus,
} from '@/utility/CommonFunctions';
import { capitalizeFirstLetter, showToastNotification } from '@/utility/helper';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

export interface IUnpaidLeaveItem {
  id?: string;
  startDate?: string;
  endDate?: string;
  reason?: string;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
  status?: number | string;
  days?: number | string;
  leaveId?: string;
  userId?: string;
  approvedBy?: string;
}

export interface ILeaveDetail {
  id: string;
  user: IUser;
  status: number;
  createdAt: string;
  startDate: string;
  endDate: string;
  leaveType: string;
  reason: string;
  documents: IDocument[];
  leaveDuration: string;
  url: string;
  isUnpaid?: boolean;
  unPaidLeave?: IUnpaidLeaveItem | null;
  unpaidLeaves?: IUnpaidLeaveItem[];
  unpaidLeave?: IUnpaidLeaveItem | null;
}
interface IDocument {
  name: string;
  url: string;
}
export interface ILeaveDetailResponse {
  status: boolean;
  message: string;
  data: ILeaveDetail;
}
interface ISubmitData {
  status: number | undefined;
  type: string;
  id: string | undefined;
  reason?: string;
}

const LeaveDetail = () => {
  const location = useLocation();
  const { id } = useParams();
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const [refreshLogs, setRefreshLogs] = useState<boolean>(false);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(
    null,
  );

  const [selectedDocument, setSelectedDocument] = useState<{
    url: string;
  } | null>(null);
  const [modal, setModal] = useState(false);
  const toggleModal = () => {
    setModal(!modal);
  };
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dataLoading, setDataLoading] = useState<boolean>(true);
  const [leaveDetail, setLeaveDetail] = useState<ILeaveDetail | null>(null);
  const [isUnpaidModalOpen, setIsUnpaidModalOpen] = useState<boolean>(false);
  const [selectedEditUnpaid, setSelectedEditUnpaid] =
    useState<IUnpaidLeaveItem | null>(null);
  const [isDeleteUnpaidModalOpen, setIsDeleteUnpaidModalOpen] =
    useState<boolean>(false);
  const [isDeletingUnpaid, setIsDeletingUnpaid] = useState<boolean>(false);

  useEffect(() => {
    GetLeaveDetail();
  }, []);

  const handleDeleteUnpaidLeave = async () => {
    const unPaidData = leaveDetail?.unPaidLeave || leaveDetail?.unpaidLeave;
    const unpaidId = unPaidData?.id;
    if (!unpaidId) {
      showToastNotification('danger', 'Unpaid leave ID not found');
      return;
    }
    try {
      setIsDeletingUnpaid(true);
      const API = `${API_URL + API_ENDPOINT.deleteUnpaidLeave}?unPaidLeaveId=${unpaidId}`;
      const response: IDeleteResponse = await callApi(API, {
        method: 'DELETE',
      });
      if (response?.status) {
        showToastNotification(
          'success',
          response?.message || 'Unpaid leave deleted successfully',
        );
        setIsDeleteUnpaidModalOpen(false);
        GetLeaveDetail();
        setRefreshLogs(true);
      } else {
        showToastNotification(
          'danger',
          response?.message || 'Failed to delete unpaid leave',
        );
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setIsDeletingUnpaid(false);
    }
  };

  const handleDocumentClick = (item: string) => {
    const docUrl = {
      url: item,
    };
    setSelectedDocument(docUrl);
    setModal(true);
  };

  const GetLeaveDetail = async () => {
    try {
      const API = API_URL + API_ENDPOINT?.getLeaveDetail + `?leaveId=${id}`;
      setDataLoading(true);
      const response: ILeaveDetailResponse = await callApi(API, {
        method: 'GET',
      });
      setDataLoading(false);
      if (response?.status === true) {
        setLeaveDetail(response?.data);
      } else {
        showToastNotification('danger', response?.message);
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setDataLoading(false);
    }
  };
  const handleApproveReject = async (type: string, reason?: string) => {
    setIsLoading(true);
    const isHalfDay =
      leaveDetail?.leaveDuration == 'firstHalf' ||
      leaveDetail?.leaveDuration == 'secondHalf';
    let newStatus;
    if (type == 'approve') {
      newStatus = 1;
    } else if (type == 'reject') {
      newStatus = 2;
    }
    let submitData: ISubmitData = {
      type: 'leave',
      id: leaveDetail?.id,
      ...(isHalfDay ? { isHalfDayStatus: newStatus } : { status: newStatus }),
      status: newStatus,
    };
    if (reason) {
      submitData = {
        type: 'leave',
        id: leaveDetail?.id,
        status: newStatus,
        reason: reason,
      };
    }
    try {
      const response: IDeleteResponse = await callApi(
        API_URL + API_ENDPOINT.userStatusUpdate,
        {
          method: 'POST',
          data: submitData,
        },
      );
      if (response?.status) {
        setIsConfirmModalOpen(false);
        showToastNotification('success', response?.message);
        GetLeaveDetail();
        setRefreshLogs(true);
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const submitConfirm = (actionType?: string, reason?: string) => {
    if (actionType === 'approve') {
      handleApproveReject('approve');
    } else if (actionType === 'reject') {
      handleApproveReject('reject', reason);
    }
  };

  return (
    <Layout>
      {dataLoading ? (
        <FullPageLoader />
      ) : (
        <>
          <div className="content container-fluid">
            <BackButton isDetail={true} label="Leave Detail" />
            <div className="card manage-user-list">
              <div className="card-body">
                <ul
                  className={
                    dataLoading
                      ? 'row list-unstyled mt-4 loading-skeleton'
                      : 'row list-unstyled mt-4'
                  }
                >
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">
                        Emp. Name :-
                      </span>
                      {capitalizeFirstLetter(leaveDetail?.user?.name ?? '--')}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">
                        Emp. Code :-
                      </span>
                      {leaveDetail?.user?.employeeCode || '--'}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">Status :-</span>
                      {handleStatus(leaveDetail?.status ?? 0) || '--'}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">
                        Emp. Dept. :-
                      </span>
                      {leaveDetail?.user?.department?.name || '--'}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">
                        Emp. Designation :-
                      </span>
                      {leaveDetail?.user?.designation?.name || '--'}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">Office :-</span>
                      {leaveDetail?.user?.site?.name || '--'}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">
                        Applied On :-
                      </span>
                      {(leaveDetail?.createdAt &&
                        moment(leaveDetail?.createdAt).format('DD/MM/YYYY')) ||
                        '--'}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">From :-</span>
                      {(leaveDetail?.startDate &&
                        moment(leaveDetail?.startDate).format('DD/MM/YYYY')) ||
                        '--'}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">To :-</span>
                      {(leaveDetail?.endDate &&
                        moment(leaveDetail?.endDate).format('DD/MM/YYYY')) ||
                        '--'}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">Days :-</span>
                      {leaveDetail?.startDate && leaveDetail?.endDate
                        ? (() => {
                            const days =
                              Number(
                                calculateDaysDifference(
                                  leaveDetail?.startDate,
                                  leaveDetail?.endDate,
                                  'number',
                                ),
                              ) || 0;
                            const result =
                              leaveDetail?.leaveDuration == 'firstHalf' ||
                              leaveDetail?.leaveDuration == 'secondHalf'
                                ? days * 0.5
                                : days;
                            return result
                              ? `${result} ${result === 1 ? 'day' : 'days'}`
                              : '--';
                          })()
                        : '--'}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">
                        Leave Type :-
                      </span>
                      {leaveDetail?.leaveType || '--'}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">
                        Leave Duration :-
                      </span>
                      {leaveDetail?.leaveDuration
                        ? leaveDetail?.leaveDuration.includes('Half')
                          ? capitalizeFirstLetter(
                              leaveDetail?.leaveDuration,
                            )?.split('Half')?.[0] +
                            ' ' +
                            'Half'
                          : leaveDetail?.leaveDuration
                        : '--'}
                    </p>
                  </li>
                  <li className="col-12 col-md-6 col-lg-4">
                    <p>
                      <span className="text-muted fs-5 d-block">
                        Leave Reason :-
                      </span>
                      {leaveDetail?.reason || '--'}
                    </p>
                  </li>
                </ul>
                {(location?.state?.type === 'request' ||
                  location?.state?.type === 'teamLeaveRequest') &&
                  leaveDetail?.status == 0 && (
                    <div className="card-body d-flex justify-content-end">
                      <div className="col-12 col-md-6 col-lg-4 ">
                        <span className="d-flex align-items-center gap-3 justify-content-end">
                          <>
                            <button
                              className="btn btn-success"
                              onClick={() => {
                                setActionType('approve');
                                setIsConfirmModalOpen(true);
                              }}
                            >
                              Approve
                            </button>
                            <button
                              className="btn btn-danger"
                              onClick={() => {
                                setActionType('reject');
                                setIsConfirmModalOpen(true);
                              }}
                            >
                              Reject
                            </button>
                          </>
                        </span>
                      </div>
                    </div>
                  )}
              </div>
            </div>
            <div className="row">
              <div className="col-12 ">
                <div className="card manage-user-list">
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between border-bottom mb-3">
                      <h4 className="d-flex align-items-center mb-4">
                        Documents
                      </h4>
                    </div>
                    <ul className="row list-unstyled gap-3">
                      {leaveDetail?.url && leaveDetail?.url ? (
                        <li className="col-12">
                          <div
                            className="text-muted fs-4 d-flex gap-2 text-decoration-none"
                            onClick={() =>
                              handleDocumentClick(leaveDetail?.url)
                            }
                            role="button"
                          >
                            <DocumentIcon />
                            {leaveDetail?.url?.split('-')[1] || 'Document'}
                          </div>
                        </li>
                      ) : (
                        <div>No Document Found</div>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Logs
            type={'leave'}
            id={id ?? ''}
            refreshLogs={refreshLogs}
            setRefreshLogs={setRefreshLogs}
          />
          <div className="content container-fluid">
            <div className="card manage-user-list">
              <div className="card-body">
                {(() => {
                  const isActionAllowed =
                    location?.state?.type === 'request' ||
                    location?.state?.type === 'teamLeaveRequest';
                  const unPaidData =
                    leaveDetail?.unPaidLeave || leaveDetail?.unpaidLeave;
                  const unpaidList = unPaidData
                    ? [unPaidData]
                    : leaveDetail?.unpaidLeaves &&
                        leaveDetail.unpaidLeaves.length > 0
                      ? leaveDetail.unpaidLeaves
                      : [];

                  return (
                    <>
                      <div className="d-flex align-items-center justify-content-between border-bottom pb-3 mb-3 flex-wrap gap-2">
                        <h4 className="mb-0">Unpaid Leave</h4>
                        {isActionAllowed &&
                          (unPaidData ? (
                            <div className="d-flex align-items-center gap-2">
                              <button
                                className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
                                type="button"
                                onClick={() => {
                                  setSelectedEditUnpaid(unPaidData);
                                  setIsUnpaidModalOpen(true);
                                }}
                              >
                                <EditIcon />
                                <span>Edit</span>
                              </button>
                              <button
                                className="btn btn-outline-danger btn-sm d-flex align-items-center gap-1"
                                type="button"
                                onClick={() => setIsDeleteUnpaidModalOpen(true)}
                              >
                                <DeleteIcon />
                                <span>Delete</span>
                              </button>
                            </div>
                          ) : (
                            <button
                              className="btn btn-primary"
                              type="button"
                              onClick={() => {
                                setSelectedEditUnpaid(null);
                                setIsUnpaidModalOpen(true);
                              }}
                            >
                              Mark as Unpaid
                            </button>
                          ))}
                      </div>

                      {unpaidList.length > 0 ? (
                        <div className="table-responsive">
                          <table className="table table-hover table-striped align-middle">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Start Date</th>
                                <th>End Date</th>
                                <th>Days</th>
                                <th>Reason</th>
                                <th>Created At</th>
                              </tr>
                            </thead>
                            <tbody>
                              {unpaidList.map((item, idx) => {
                                const days =
                                  Number(
                                    calculateDaysDifference(
                                      item?.startDate || '',
                                      item?.endDate || '',
                                      'number',
                                    ),
                                  ) || 0;
                                return (
                                  <tr key={item?.id || idx}>
                                    <td>{idx + 1}</td>
                                    <td>
                                      {item?.startDate
                                        ? moment(item.startDate).format(
                                            'DD/MM/YYYY',
                                          )
                                        : '--'}
                                    </td>
                                    <td>
                                      {item?.endDate
                                        ? moment(item.endDate).format(
                                            'DD/MM/YYYY',
                                          )
                                        : '--'}
                                    </td>
                                    <td>
                                      {days
                                        ? `${days} ${days === 1 ? 'day' : 'days'}`
                                        : '--'}
                                    </td>
                                    <td>{item?.reason || '--'}</td>
                                    <td>
                                      {item?.createdAt
                                        ? moment(item.createdAt).format(
                                            'DD/MM/YYYY',
                                          )
                                        : '--'}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-muted py-2">
                          {isActionAllowed
                            ? 'No unpaid leave records found for this leave request. Click Mark as Unpaid to convert leave days into unpaid leave.'
                            : 'No unpaid leave records found for this leave request.'}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
          <ViewDocumentModal
            isOpen={modal}
            toggle={toggleModal}
            selectedDocument={selectedDocument}
          />
          <ApproveModal
            isOpen={isConfirmModalOpen}
            isLoading={isLoading}
            submitHandler={submitConfirm}
            toggle={() => setIsConfirmModalOpen(false)}
            actionType={actionType ?? ''}
          />
          <ConfirmModal
            isOpen={isDeleteUnpaidModalOpen}
            toggle={() => setIsDeleteUnpaidModalOpen(false)}
            isLoading={isDeletingUnpaid}
            hideReason={true}
            submitHandler={handleDeleteUnpaidLeave}
          />
          <MarkUnpaidLeaveModal
            isOpen={isUnpaidModalOpen}
            toggle={() => {
              setIsUnpaidModalOpen(!isUnpaidModalOpen);
              if (isUnpaidModalOpen) setSelectedEditUnpaid(null);
            }}
            leaveDetail={leaveDetail}
            editData={selectedEditUnpaid}
            onSuccess={() => {
              GetLeaveDetail();
              setRefreshLogs(true);
              setSelectedEditUnpaid(null);
            }}
          />
        </>
      )}
    </Layout>
  );
};

export default LeaveDetail;
